import { supabase } from "@/lib/supabase";
import { isAdminDevice } from "@/lib/admin-exclude";
import { gaEvent, gaPageView } from "@/lib/ga";

const SESSION_KEY = "pdn_session_id";
const GEO_KEY = "pdn_geo_v1";
const GEO_TTL_MS = 1000 * 60 * 60 * 24; // 24h

type Geo = { country?: string; country_code?: string; city?: string };

function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2);
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

let geoPromise: Promise<Geo> | null = null;

async function getGeo(): Promise<Geo> {
  try {
    const cached = localStorage.getItem(GEO_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.t && Date.now() - parsed.t < GEO_TTL_MS) return parsed.g as Geo;
    }
  } catch {}

  if (!geoPromise) {
    geoPromise = (async () => {
      const cacheResult = (g: Geo) => {
        try { localStorage.setItem(GEO_KEY, JSON.stringify({ t: Date.now(), g })); } catch {}
        return g;
      };
      try {
        const res = await fetch("https://api.country.is/", { cache: "force-cache" });
        if (!res.ok) return cacheResult({});
        const j = await res.json();
        return cacheResult({ country: j?.country, country_code: j?.country });
      } catch {
        return cacheResult({});
      }
    })();
  }
  return geoPromise;
}

export async function trackPageView(path: string) {
  if (isAdminDevice()) return;
  gaPageView(path);
  try {
    const [geo] = await Promise.all([getGeo()]);
    await supabase.from("page_views").insert({
      path,
      referrer: document.referrer || null,
      country: geo.country ?? null,
      country_code: geo.country_code ?? null,
      city: geo.city ?? null,
      session_id: getSessionId(),
      user_agent: navigator.userAgent.slice(0, 255),
    });
  } catch {}
}

export async function trackSearch(query: string, results_count = 0) {
  if (isAdminDevice()) return;
  const q = query.trim();
  if (!q) return;
  gaEvent("search", { search_term: q.slice(0, 200), results_count });
  try {
    const geo = await getGeo();
    await supabase.from("searches").insert({
      query: q.slice(0, 200),
      results_count,
      country: geo.country ?? null,
      country_code: geo.country_code ?? null,
      session_id: getSessionId(),
    });
  } catch {}
}

export async function trackDomainView(slug: string) {
  if (isAdminDevice()) return;
  gaEvent("domain_view", { domain_slug: slug });
  try {
    const geo = await getGeo();
    await supabase.from("views").insert({
      domain_slug: slug,
      country: geo.country ?? null,
      country_code: geo.country_code ?? null,
      session_id: getSessionId(),
    });
  } catch {}
}

export async function trackDomainClick(slug: string, type: "click" | "buy" | "offer" | "lead" = "click") {
  if (isAdminDevice()) return;
  gaEvent(`domain_${type}`, { domain_slug: slug, click_type: type });
  try {
    const geo = await getGeo();
    await supabase.from("clicks").insert({
      domain_slug: slug,
      type,
      country: geo.country ?? null,
      country_code: geo.country_code ?? null,
      session_id: getSessionId(),
    });
  } catch {}
}
